import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { LoadingService } from '../shared/fi-loading/loading.service';
import { ILightWeightObject, ILightWeightObjectCollection } from '@fi-shared/models';
import {
    ISiteMapConfirmDeleteData,
    SiteMapConfirmDeleteComponent,
} from './site-map-confirm-delete/site-map-confirm-delete.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IItemAccess, IItemAvailability } from '@fi-shared/models/common';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import { ISiteMap } from '@fi-shared/models/site-map';
import { ObjectTypes } from '@fi-shared/types';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';

@Injectable()
export class SiteMapService {
    constructor(
        private fiBackend: FiBackendService,
        private fiLoadingService: LoadingService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
    ) {}

    getSiteMapsCount(parameterApiModel: IItemsListParameterBase, reqConfig?) {
        return this.fiBackend.fetchDataPost<number>('SiteMap/SiteMap/GetSiteMapsCount', parameterApiModel, reqConfig);
    }

    getSiteMapsListData(parameter: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<any>>(
            'SiteMap/SiteMap/GetSiteMapsListData',
            parameter,
        );
    }

    getSiteMaps(parameterApiModel: IItemsListParameterBase, reqConfig?) {
        return this.fiBackend.fetchDataPost<ISiteMap[]>('SiteMap/SiteMap/GetSiteMaps', parameterApiModel, reqConfig);
    }

    GetLightWeightObjectsForLocalSearch(parameterApiModel: IItemsListParameterBase, reqConfig?) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            'SiteMap/SiteMap/GetLightWeightObjectsForLocalSearch',
            parameterApiModel,
            reqConfig,
        );
    }

    getLightWeightSiteMaps() {
        return this.fiBackend.fetchData<ILightWeightObject[]>('SiteMap/SiteMap/GetLightWeightSiteMaps');
    }

    getLightWeightSiteMap(siteMapId: number) {
        return this.fiBackend.fetchData<ILightWeightObject>(`SiteMap/SiteMap/GetLightWeightSiteMap/${siteMapId}`);
    }

    getSiteMapForEdit(id: number) {
        return this.fiBackend.fetchData<ISiteMap>(`SiteMap/SiteMap/GetSiteMap/${id}?isEditMode=true`);
    }

    getSiteMap(id: number) {
        return this.fiBackend.fetchData<ISiteMap>('SiteMap/SiteMap/GetSiteMap/' + id);
    }

    getSiteMapForWidget(id: number) {
        return this.fiBackend.fetchData<ISiteMap>('SiteMap/SiteMap/GetSiteMapForWidget/' + id);
    }

    checkSiteMapAvailable(id: number) {
        return this.fiBackend.fetchData<IItemAvailability>('SiteMap/SiteMap/IsSiteMapAvailable/' + id);
    }

    checkHasEditAccessToSiteMap(id: number) {
        return this.fiBackend.fetchData<IItemAccess>('SiteMap/SiteMap/HasEditAccessToSiteMap/' + id);
    }

    saveSiteMap(siteMap: ISiteMap) {
        return this.fiBackend.fetchDataPost('SiteMap/SiteMap/SaveSiteMap', siteMap);
    }

    getSiteMapItemsByTagId(tagId: number) {
        return this.fiBackend.fetchData('SiteMap/SiteMap/GetSiteMapItemsByTagId/' + tagId);
    }

    deleteSiteMaps(ids: number[]) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchDataPost<void>('SiteMap/SiteMap/DeleteSiteMaps', ids),
        );
    }

    deleteSiteMapAction(ids: number[], items: ISiteMap[]) {
        const itemsInUse = items.filter((item) => item.IsInUse);
        if (itemsInUse.length == 0)
            return this.deleteSiteMaps(ids).then(() => {
                this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                    ObjectTypes.SiteMap,
                    ids,
                    items[0].Name,
                );
            });

        const isPlural = items.length > 1;

        const promise = new Promise<void>((resolve, reject) => {
            const dialogContentData: ISiteMapConfirmDeleteData = {
                message: isPlural ? 'sitemap.delete_multiple_warning' : 'sitemap.delete_warning',
                varX: itemsInUse.length,
                varY: items.length,
                confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                yesButtonText: this.appTextService.get('common.confirm').toUpperCase(),
            };
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('common.warning'),
                    contentComponentRef: SiteMapConfirmDeleteComponent,
                    contentComponentData: dialogContentData,
                    config: {
                        maxWidth: '400px',
                    },
                })
                .onYesButtonClick(() => {
                    this.deleteSiteMaps(ids).then((response) => {
                        this.fiDialogsService.notify({
                            messageText: this.appTextService.get(
                                isPlural ? 'sitemap.delete_multiple_response' : 'sitemap.delete_response',
                            ),
                        });
                        resolve(response);
                    }, reject);
                })
                .onNoButtonClick(() => reject());
        });

        return promise;
    }
}
